/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect} from "react";
import CardComponent from "pages/Front/Booking/components/CardComponent";
import {PercentBadgeIcon} from "@heroicons/react/24/outline";
import clsx from "clsx";
import {CheckIcon} from "@heroicons/react/24/solid";
import {useDispatch} from "useDispatch";
import {useTranslation} from "react-i18next";
import {Reservation} from "types/entities/Reservation";
import {PackagesCartItemVoucher} from "types/entities/PackagesCartItemVoucher";
import {currencyNumberFormat} from "utils/utility";
import {usePackagesCartPricing} from "utils/packageUtils";
import {PackageItemItemType} from "types/entities/PackageItem";
import {useForm} from "react-hook-form";
import {toast} from "react-toastify";
import {closeModal} from "helpers/actions";
import ModalScrollableClose from "components/ModalScrollableClose";
import SellerAction from "store/seller/SellerAction";

interface BookingSellerVoucherModalProps {
    packagesCartItemVoucher: PackagesCartItemVoucher;
    reservation: Reservation;
}

const BookingSellerVoucherModal = (props: BookingSellerVoucherModalProps) => {
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const {priceByType} = usePackagesCartPricing(props.reservation);

    // const [upsellPriceDiscount, setUpsellPriceDiscount] = React.useState<number>(priceDiscount / 100 || 0);
    const [upsellPriceDiscount, setUpsellPriceDiscount] = React.useState<number>(props.packagesCartItemVoucher.price / 100 || 0);
    const inputClass = "flex rounded-md border leading-tight w-full py-1 px-2 h-[30px]";

    const {register, setValue, watch} = useForm({
        defaultValues: {
            priceDiscount: props.packagesCartItemVoucher.price / 100 || 0,
        },
    });
    const changeDiscountHandler = () => {
        //this is a simple dispatch on backend
        dispatch(SellerAction.fetchChangeVoucherPrice(props.reservation.id, props.packagesCartItemVoucher, "price", upsellPriceDiscount * 100)).then(r => {
            // dispatch(SellerAction.setReservation(r.reservation));
            dispatch(closeModal());
            toast.success(t("Upsell changed"));
        });
    };

    const priceDiscount = watch("priceDiscount");

    const onChangePriceDiscountHandler = (newValue: number) => {
        setUpsellPriceDiscount(+newValue);
    };

    useEffect(() => {
        onChangePriceDiscountHandler(+priceDiscount);
    }, [priceDiscount]);

    return (
        <CardComponent
            className={"BookingSellerDiscountModal w-full md:min-w-[400px] max-w-[500px] !p-0 border border-gray-200 relative overflow-hidden h-auto max-h-full flex flex-col"}>
            <div
                className={"top-0 z-30 sticky flex flex-row justify-between rounded-tl-lg rounded-tr-lg w-full px-3 py-3 font-bold border-b-2 border-gray-200 bg-gray-50 text-gray-900 drop-shadow-sm"}>
                <div className={"flex flex-row items-center"}>
                    <PercentBadgeIcon className={clsx("w-5 h-5 mr-1 text-gray-600/70 drop-shadow-xl")} />
                    <h4 className={"font-medium text-gray-700"}>{t("Discount")}</h4>
                </div>
                <ModalScrollableClose />
            </div>


            <div
                className={"flex w-full flex-col px-3 pt-3 pb-4 bg-white overflow-hidden h-auto rounded-bl-lg rounded-br-lg"}>
                <div className={"flex flex-col overflow-y-auto h-full"}>
                    <h4 className={"font-bold text-lg"}>{t("Item information")}</h4>
                    <div className={"mb-6"}>
                        <table className={"w-full"}>
                            <tbody>
                            <tr className={"border-b border-dashed"}>
                                <td className={"font-medium"}>{"Total price of items"}</td>
                                <td className={"text-right"}>{currencyNumberFormat(priceByType(PackageItemItemType.PackageItem))}</td>
                            </tr>
                            <tr className={"border-b border-dashed"}>
                                <td className={"font-medium"}>{"Total price of upsells"}</td>
                                <td className={"text-right"}>{currencyNumberFormat(priceByType(PackageItemItemType.Upsell))}</td>
                            </tr>
                            <tr className={"border-b border-dashed"}>
                                <td className={"font-medium"}>{"Current discount"}</td>
                                <td className={"text-right"}>{currencyNumberFormat(props.packagesCartItemVoucher.price)}</td>
                            </tr>
                            </tbody>
                        </table>
                    </div>

                    <div
                        className={"Actions grid grid-cols-3 gap-x-2 gap-y-3 border rounded-lg px-3 py-3 shadow bg-gray-50"}>
                        <div className={"cols-span-1"}>
                            <label className={"inline-flex mb-1 font-bold"} htmlFor={"priceDiscount"}>
                                {t("CHF discount")}
                            </label>
                            <input
                                className={clsx(inputClass)}
                                {...register("priceDiscount")}
                                type="number"
                                min={0}
                                max={1000}
                                step={1}
                                placeholder={currencyNumberFormat(priceDiscount)}
                                name="priceDiscount"
                            />
                        </div>

                        <div className={"col-span-3 text-center"}>
                            <button
                                className={clsx(
                                    "border-green-400 bg-green-400 text-white mx-auto flex items-center justify-center font-bold leading-tight py-2 px-4 rounded-md",
                                )}
                                onClick={changeDiscountHandler}
                            >
                                <CheckIcon className={"w-5 h-5 flex-none mr-2"} />
                                <span>{t("Apply discount")}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </CardComponent>
    );
};

export default BookingSellerVoucherModal;
